import { supabase } from '@/utils/supabase';
import type {
  LoanDataExportCandidate,
  LoanDataExportCandidateSearchInput,
  LoanDataExportFilterOption,
  LoanDataExportFilterOptions,
  LoanDataExportGenerationResult,
  LoanDataExportHistoryPage,
  LoanDataExportPreflight,
  LoanDataExportTemplate,
  LoanDataExportTemplateId
} from '@/types/loan-data-export';

const FUNCTION_NAME = 'loan-data-export';

export class LoanDataExportServiceError extends Error {
  status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'LoanDataExportServiceError';
    this.status = status;
  }
}

interface CandidateRow {
  id: string;
  encompass_loan_id: string | null;
  loan_number: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  loan_folder: string | null;
  loan_status: string | null;
  loan_purpose: string | null;
  loan_amount: number | string | null;
  property_address: string | null;
  property_city: string | null;
  property_state: string | null;
  loan_officer_name: string | null;
  product_category?: string | null;
  purpose_category?: string | null;
  investor_name?: string | null;
  loan_program?: string | null;
  channel?: string | null;
  channel_category?: string | null;
  non_qm_doc_type?: string | null;
  lien_position?: string | null;
  rate_type?: string | null;
  aus_decision?: string | null;
  closing_date?: string | null;
  funded_date?: string | null;
  application_date?: string | null;
  created_date?: string | null;
  last_modified_date?: string | null;
  total_count?: number | string;
}

function mapCandidate(row: CandidateRow): LoanDataExportCandidate {
  return {
    id: row.id,
    encompassLoanId: row.encompass_loan_id ?? '',
    loanNumber: row.loan_number ?? '',
    borrowerFirstName: row.borrower_first_name,
    borrowerLastName: row.borrower_last_name,
    loanFolder: row.loan_folder,
    loanStatus: row.loan_status,
    loanPurpose: row.loan_purpose,
    loanAmount: row.loan_amount === null ? null : Number(row.loan_amount),
    propertyAddress: row.property_address,
    propertyCity: row.property_city,
    propertyState: row.property_state,
    loanOfficerName: row.loan_officer_name,
    productCategory: row.product_category ?? null,
    purposeCategory: row.purpose_category ?? null,
    investorName: row.investor_name ?? null,
    loanProgram: row.loan_program ?? null,
    channel: row.channel ?? null,
    channelCategory: row.channel_category ?? null,
    nonQmDocType: row.non_qm_doc_type ?? null,
    lienPosition: row.lien_position ?? null,
    rateType: row.rate_type ?? null,
    ausDecision: row.aus_decision ?? null,
    closingDate: row.closing_date ?? null,
    fundedDate: row.funded_date ?? null,
    applicationDate: row.application_date ?? null,
    createdDate: row.created_date ?? null,
    lastModifiedDate: row.last_modified_date ?? null
  };
}

function filterOptions(value: unknown): LoanDataExportFilterOption[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((option) => {
    if (!option || typeof option !== 'object') {
      return [];
    }
    const raw = option as { value?: unknown; label?: unknown; count?: unknown };
    if (typeof raw.value !== 'string' || raw.value.length === 0) {
      return [];
    }
    return [{
      value: raw.value,
      label: typeof raw.label === 'string' && raw.label.length > 0 ? raw.label : raw.value,
      count: Number(raw.count) || 0
    }];
  });
}

function mapFilterOptions(value: unknown): LoanDataExportFilterOptions {
  const raw = value && typeof value === 'object'
    ? value as Record<string, unknown>
    : {};
  return {
    totalEligible: Number(raw.totalEligible) || 0,
    productCategories: filterOptions(raw.productCategories),
    investors: filterOptions(raw.investors),
    folders: filterOptions(raw.folders),
    purposeCategories: filterOptions(raw.purposeCategories),
    purposes: filterOptions(raw.purposes),
    programs: filterOptions(raw.programs),
    channelCategories: filterOptions(raw.channelCategories),
    loanOfficers: filterOptions(raw.loanOfficers),
    statuses: filterOptions(raw.statuses),
    states: filterOptions(raw.states),
    nonQmDocTypes: filterOptions(raw.nonQmDocTypes),
    lienPositions: filterOptions(raw.lienPositions),
    rateTypes: filterOptions(raw.rateTypes),
    ausDecisions: filterOptions(raw.ausDecisions)
  };
}

async function invokeExportFunction<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, { body });
  if (error) {
    let message = error.message || 'Data export request failed';
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const payload = await context.clone().json() as { error?: string };
        message = payload.error || message;
      } catch {
        // Keep the function client error when the response is not JSON.
      }
    }
    throw new LoanDataExportServiceError(message, context?.status ?? null);
  }
  return data as T;
}

export async function getLoanDataExportCatalog(): Promise<LoanDataExportTemplate[]> {
  const response = await invokeExportFunction<{ templates: LoanDataExportTemplate[] }>({
    action: 'catalog'
  });
  return response.templates;
}

export async function searchLoanDataExportCandidates(
  input: LoanDataExportCandidateSearchInput
): Promise<{ items: LoanDataExportCandidate[]; totalCount: number }> {
  const page = Math.max(1, input.page ?? 1);
  const pageSize = Math.min(100, Math.max(1, input.pageSize ?? 25));
  const { data, error } = await supabase.rpc('search_loan_data_export_candidates', {
    p_search: input.search?.trim() || null,
    p_folder: null,
    p_purpose: null,
    p_folders: input.folders?.length ? input.folders : null,
    p_purpose_categories: input.purposeCategories?.length ? input.purposeCategories : null,
    p_purposes: input.purposes?.length ? input.purposes : null,
    p_product_categories: input.productCategories?.length ? input.productCategories : null,
    p_investors: input.investors?.length ? input.investors : null,
    p_programs: input.programs?.length ? input.programs : null,
    p_channel_categories: input.channelCategories?.length ? input.channelCategories : null,
    p_loan_officers: input.loanOfficers?.length ? input.loanOfficers : null,
    p_statuses: input.statuses?.length ? input.statuses : null,
    p_states: input.states?.length ? input.states : null,
    p_non_qm_doc_types: input.nonQmDocTypes?.length ? input.nonQmDocTypes : null,
    p_lien_positions: input.lienPositions?.length ? input.lienPositions : null,
    p_rate_types: input.rateTypes?.length ? input.rateTypes : null,
    // Sent only when chosen, so this page keeps working against a database
    // that predates the AUS decision filter.
    ...(input.ausDecisions?.length ? { p_aus_decisions: input.ausDecisions } : {}),
    p_min_amount: input.minAmount ?? null,
    p_max_amount: input.maxAmount ?? null,
    p_date_field: input.dateField ?? null,
    p_date_from: input.dateFrom || null,
    p_date_to: input.dateTo || null,
    p_offset: (page - 1) * pageSize,
    p_limit: pageSize
  });
  if (error) {
    throw new Error(error.message);
  }
  const rows = (data ?? []) as CandidateRow[];
  return {
    items: rows.map(mapCandidate),
    totalCount: rows.length > 0 ? Number(rows[0].total_count ?? rows.length) : 0
  };
}

export async function getLoanDataExportFilterOptions(): Promise<LoanDataExportFilterOptions> {
  const { data, error } = await supabase.rpc('get_loan_data_export_filter_options');
  if (error) {
    throw new Error(error.message);
  }
  return mapFilterOptions(data);
}

export async function resolveLoanDataExportNumbers(
  loanNumbers: string[]
): Promise<LoanDataExportCandidate[]> {
  const normalized = Array.from(new Set(loanNumbers.map((number) => number.trim()).filter(Boolean)));
  if (normalized.length === 0) {
    return [];
  }
  if (normalized.length > 1000) {
    throw new Error('A maximum of 1,000 loan numbers can be resolved at once');
  }
  const { data, error } = await supabase.rpc('resolve_loan_data_export_numbers', {
    p_loan_numbers: normalized
  });
  if (error) {
    throw new Error(error.message);
  }
  return ((data ?? []) as CandidateRow[]).map(mapCandidate);
}

export async function resolveLoanDataExportIds(
  loanIds: string[]
): Promise<LoanDataExportCandidate[]> {
  const normalized = Array.from(new Set(
    loanIds.map((loanId) => loanId.trim()).filter(Boolean)
  ));
  if (normalized.length === 0) {
    return [];
  }
  if (normalized.length > 1000) {
    throw new Error('A maximum of 1,000 loan IDs can be resolved at once');
  }

  const batches: string[][] = [];
  for (let index = 0; index < normalized.length; index += 100) {
    batches.push(normalized.slice(index, index + 100));
  }
  const rows = await Promise.all(batches.map(async (batch) => {
    const { data, error } = await supabase
      .from('encompass_loans')
      .select(`
        id,
        encompass_loan_id,
        trans_details_loan_number,
        borrower_first_name,
        borrower_last_name,
        loan_folder,
        trans_details_loan_status,
        trans_details_loan_purpose,
        trans_details_loan_amt,
        property_address,
        subject_property_city,
        subject_property_state,
        loan_officer_first_name,
        loan_officer_last_name
      `)
      .in('id', batch);
    if (error) {
      throw new Error(error.message);
    }
    return data ?? [];
  }));

  return rows.flat().map((row) => ({
    id: row.id,
    encompassLoanId: row.encompass_loan_id ?? '',
    loanNumber: row.trans_details_loan_number ?? '',
    borrowerFirstName: row.borrower_first_name,
    borrowerLastName: row.borrower_last_name,
    loanFolder: row.loan_folder,
    loanStatus: row.trans_details_loan_status,
    loanPurpose: row.trans_details_loan_purpose,
    loanAmount: row.trans_details_loan_amt === null
      ? null
      : Number(row.trans_details_loan_amt),
    propertyAddress: row.property_address,
    propertyCity: row.subject_property_city,
    propertyState: row.subject_property_state,
    loanOfficerName: [
      row.loan_officer_first_name,
      row.loan_officer_last_name
    ].filter(Boolean).join(' ') || null
  }));
}

export function preflightLoanDataExport(
  loanIds: string[],
  templateId: LoanDataExportTemplateId
): Promise<LoanDataExportPreflight> {
  return invokeExportFunction<LoanDataExportPreflight>({
    action: 'preflight',
    loanIds,
    templateId
  });
}

export function generateLoanDataExport(input: {
  loanIds: string[];
  templateId: LoanDataExportTemplateId;
  requestId: string;
  acknowledgeWarnings: boolean;
}): Promise<LoanDataExportGenerationResult> {
  return invokeExportFunction<LoanDataExportGenerationResult>({
    action: 'generate',
    ...input
  });
}

export function getLoanDataExportHistory(
  page = 1,
  pageSize = 25
): Promise<LoanDataExportHistoryPage> {
  return invokeExportFunction<LoanDataExportHistoryPage>({
    action: 'history',
    page,
    pageSize
  });
}

export function getLoanDataExportDownload(
  exportId: string
): Promise<LoanDataExportGenerationResult> {
  return invokeExportFunction<LoanDataExportGenerationResult>({
    action: 'download',
    exportId
  });
}

export async function downloadLoanDataExport(
  signedUrl: string,
  fileName: string,
  options: { shouldContinue?: () => boolean } = {}
): Promise<Blob | null> {
  const response = await fetch(signedUrl);
  if (!response.ok) {
    throw new Error('The secure download link could not be retrieved');
  }
  const blob = await response.blob();
  // The fetch and blob reads can outlast the page or the signed-in user, so
  // the caller gets a last word right before the browser saves the file.
  if (options.shouldContinue && !options.shouldContinue()) {
    return null;
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return blob;
}
